<template>
    <div class="home-user-manager">
        <header class="header"><span>Manage Users</span></header>
        <div class="main">
            <template v-if="showingResults">
                <template v-if="data.length">
                    <div class="table-container">
                        <table class="standard-table">
                            <thead>
                            <tr>
                                <th>
                                    <div>User</div>
                                </th>
                                <th>
                                    <div>Is Manager</div>
                                </th>
                                <th>
                                    <div>Access to Projects</div>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(item,index) in data" :key="index">
                                <td>
                                    <div class="text-capitalize">{{ item.username }}</div>
                                </td>
                                <td>
                                    <div>
                                        <label class="mr-4"><input type="radio"
                                                                   v-model="item.is_manager"
                                                                   @change="onChange($event,index)"
                                                                   :value="true"> Yes</label>
                                        <label><input type="radio"
                                                      v-model="item.is_manager"
                                                      @change="onChange($event,index)"
                                                      :value="false">
                                            No</label>
                                    </div>
                                </td>
                                <td class="column-remaining-width">
                                    <div>
                                        <label class="mr-4">
                                            <input type="radio"
                                                   v-model="item.access_to_all_projects"
                                                   :value="true" @change="onAccessToAllProjectsToggleChange(item)"
                                            > All
                                        </label>
                                        <simple-dropdown :click-dropdown-menu-to-hide="false" :dropdown-arrow="false"
                                                         theme="light"
                                                         :mouse-enter-to-show="false"
                                                         :alignment="1"
                                                         :dropdown-menu-offset="-24"
                                                         ref="chooseProjectsDropdowns"
                                        >
                                            <template v-slot:triggerButtonContent>
                                                    <span class="display-inline-flex align-items-center cursor-pointer" @click="doSpecifyUserAccessibleProjects(item)">
                                                        <input type="radio"
                                                               v-model="item.access_to_all_projects"
                                                               :value="false" class="mr-1"
                                                               @change="onAccessToAllProjectsToggleChange(item)"
                                                        >
                                                        <span v-if="item.access_to_all_projects" class="text-gray">Choose Projects..</span>
                                                        <span v-else-if="item.accessible_projects.length" :title="item.accessible_projects | projectIdsToNames">{{
                                                                item.accessible_projects | projectIdsToNames(4, false)
                                                            }}</span>
                                                        <span v-else>None</span>
                                                    </span>
                                            </template>
                                            <template v-slot:dropdownMenu>
                                                <div class="dropdown-content">
                                                    <div class="projects-chooser">
                                                        <header class="chooser-header">
                                                                <span class="header-text">
                                                                    Choose Project..
                                                                </span>
                                                            <button class="button-primary"
                                                                    @click="closeChooseProjectsDropdowns">Done
                                                            </button>
                                                        </header>
                                                        <hr>
                                                        <div class="chooser-body">
                                                            <div class="label-spacing mb-1">
                                                                <button class="mr-2" @click="selectAllAccessibleProjects(item)"
                                                                >Select All</button>
                                                                <button @click="clearAllAccessibleProjects(item)">Clear</button>
                                                            </div>
                                                            <div class="checkboxes">
                                                                <div v-for="(project,index) in allProjectOptions"
                                                                     :key="index">
                                                                    <label><input type="checkbox" :value="project.value"
                                                                                  @change="syncUserAccessToProjects(item)"
                                                                                  v-model="item.accessible_projects">
                                                                        {{ project.label }}</label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>
                                        </simple-dropdown>
                                    </div>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </template>
                <template v-else>
                    <div class="global-message-box">
                        <div>No Users to Manage</div>
                    </div>
                </template>
            </template>
            <template v-else-if="isLoading">
                <div class="global-message-box">
                    <spinner></spinner>
                </div>
            </template>
            <template v-else-if="isHavingProblemLoading">
                <div class="global-message-box">
                    <span>There was something not right while loading users.</span>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import {getFullUsersRoles, setUserAsManager, setUserAccessToProjects} from "@/apis";
import Spinner from "@/components/Spinner";
import SimpleDropdown from "@/components/SimpleDropdown";
import {allProjectOptions, projectIdToName} from "@/config";

export default {
    name: "HomeUserManager",
    components: {SimpleDropdown, Spinner},
    created() {
        this.loadData();
    },
    methods: {
        onAccessToAllProjectsToggleChange(item) {
            if (item.access_to_all_projects) {
                item.accessible_projects = [];
            }
            this.syncUserAccessToProjects(item);
        },
        syncUserAccessToProjects(item) {
            setUserAccessToProjects(item.username, item.access_to_all_projects, item.accessible_projects).catch(() => {
                alert('Error updating the user\'s access to projects.')
            })
        },
        closeChooseProjectsDropdowns() {
            const {chooseProjectsDropdowns} = this.$refs;
            if (Array.isArray(chooseProjectsDropdowns)) {
                chooseProjectsDropdowns.forEach(d => {
                    d.toggleDropdownMenu(false);
                })
            }
        },
        onChange(event, index) {
            const {username, is_manager} = this.data[index];
            setUserAsManager(username, is_manager).catch(() => {
                this.data[index].is_manager = !is_manager;
            })
        },
        setLoadingStatus(isLoading, isHavingProblemLoading, showingResults) {
            this.isLoading = isLoading;
            this.isHavingProblemLoading = isHavingProblemLoading;
            this.showingResults = showingResults;
        },
        loadData() {
            this.setLoadingStatus(true, false, false);
            getFullUsersRoles().then(r => {
                r.data.forEach(item => {
                    item.accessible_projects = item.accessible_projects || [];
                })
                this.data = r.data;
                this.setLoadingStatus(false, false, true);
            }).catch((err) => {
                this.setLoadingStatus(false, true, false);
                this.handleServerSideError(err);
            })
        },
        loadDataSilently() {
            getFullUsersRoles().then(r => {
                this.data = r.data;
            }).catch((err) => {
                this.handleServerSideError(err);
            })
        },
        doSpecifyUserAccessibleProjects(item) {
            if (!item.access_to_all_projects) {
                return;
            }
            item.access_to_all_projects = false;
        },
        selectAllAccessibleProjects(item) {
            item.accessible_projects = allProjectOptions.map(v => (v.value));
            this.syncUserAccessToProjects(item);
        },
        clearAllAccessibleProjects(item) {
            item.accessible_projects = [];
            this.syncUserAccessToProjects(item);
        }
    },
    data() {
        return {
            data: [],
            isLoading: false,
            isHavingProblemLoading: false,
            showingResults: false,
            allProjectOptions: allProjectOptions,
        }
    },
    watch: {
        $route(newVal, oldVal) {
            if (newVal.name === 'home' && oldVal.name !== 'home') {
                this.loadDataSilently();
            }
        }
    },
    filters: {
        projectIdsToNames(projectIds, maxItems, useShortName) {
            let names = projectIds.map(v => (projectIdToName[v]));
            let padding = '';
            if (typeof maxItems !== 'undefined') {
                if (names.length > maxItems) {
                    padding = ` and ${names.length - maxItems} more`
                }
                names = names.slice(0, maxItems);
            }
            if (useShortName) {
                names = names.map(name => {
                    const parts = name.split(' ');
                    if (parts.length > 1) {
                        return parts.map(part => {
                            const upc = part.match(/[A-Z]+/);
                            if (upc) {
                                return upc[0];
                            }
                            return part[0];
                        }).join('')
                    }
                    return name;
                })
            }
            return names.join(', ') + padding;
        }
    }
}
</script>

<style scoped lang="scss">
.home-user-manager {
    height: 100%;
    overflow: auto;
}

.header {
    height: 90px;
    align-items: center;
    display: flex;
    padding-left: 1.5rem;

    span {
        font-size: 1.5rem;
    }
}

.main {
    //height: calc(100% - 90px);
    padding: 0 1rem;
}

.table-container {
    /*height: 100%;*/
    /*overflow: auto;*/
    padding-bottom: 180px;
}

table {
    thead {
        th {
            text-align: left;
        }
    }

    td {
        height: 2.5rem;
        white-space: nowrap;
        min-width: 200px;

        &.column-remaining-width {
            width: 999px;
        }
    }
}

label {
    display: inline-flex;
    align-items: center;

    input {
        margin-right: 0.25rem;
    }
}

.projects-chooser {
    min-width: 240px;

    .chooser-header {
        padding: 0.25rem 0.8rem;
        display: flex;
        align-items: center;

        .header-text {
            margin-right: auto;
            color: gray;
            font-weight: 500;
        }
    }

    hr {
        margin-top: 0.15rem;
        margin-bottom: 0.5rem;
    }

    .chooser-body {
        padding: 0.25rem 0;

        label {
            display: flex;
            align-items: center;
            padding: 0.35rem 0.8rem;

            &:hover {
                background-color: rgba(black, 0.06);
            }

            input {
                margin-right: 0.25rem;
            }
        }

        .label-spacing {
            padding: 0 0.8rem;
        }
    }
}
</style>
